import React, { CSSProperties, Fragment, useMemo } from 'react';
import type { Property } from 'csstype';
import { getPrintedTextColor } from '../../util/foilColors';
import { radiansToDegrees } from '../../../../util/angleConversion';
import { Bleed, DEFAULT_CARD_BLEED } from '../../util/getCardBleed';
import { calculateLineHeight } from '../../../../util/font/calculateLineHeight';
import { textLayerTopSpacing } from '../../../../util/font/fontCalibration/textLayerTopSpacing';
import { getFontLigatures } from '../../../../util/font/fontLigatures';
import { CurvedText } from './CurvedText';
import { getPlaceCardAlignment, getElementAlignmentCss } from './textAlignmentCss';

import styles from './textElement.module.less';
import { isCurvedTextElementView, TextElementView } from '../../types/ElementView';
import { getFontAttributesFromTextElementView } from '../../util/elementViewHelpers';

type TextElementProps = {
  element: TextElementView;
  bleed?: Bleed;
  foilColor?: string;
  isPlaceCard?: boolean;
  isBoundingBoxAdjusted: boolean;
};

export const TextElement = ({
  element,
  bleed,
  foilColor = '',
  isPlaceCard = false,
  isBoundingBoxAdjusted,
}: TextElementProps) => {
  const body = useMemo(() => {
    if (element.tag === 'RECIPIENT_ADDRESS') {
      return [<Fragment />];
    }

    const lines = (element.body || '').split('\n');
    return lines.map((s, i) => (
      <Fragment key={i}>
        {s}
        {i !== lines.length - 1 && <br />}
      </Fragment>
    ));
  }, [element.body, element.tag]);

  const style: CSSProperties = useMemo(() => {
    const { letterSpacing, fontColor, lineHeight, fontSize, fontFamily, fontWeight, fontStyle } =
      getFontAttributesFromTextElementView(element);

    const originalTextColor = fontColor || 'black';
    const textColor = getPrintedTextColor(originalTextColor, element.printing_technique, foilColor);

    const { verticalAlignment, horizontalAlignment } = getElementAlignmentCss(element);
    const placeCardAlignment = isPlaceCard
      ? getPlaceCardAlignment(verticalAlignment, horizontalAlignment)
      : {};

    /*
     * Most card templates include the bleed (area cut off after printing) in their dimensions.
     * To accurately show what the printed result will be, we subtract the bleed value from our
     * preview dimensions, and must therefore adjust element positions by the same amount.
     */
    const leftBleed = bleed ? bleed.left : DEFAULT_CARD_BLEED;
    const topBleed = bleed ? bleed.top : DEFAULT_CARD_BLEED;
    const elementLeftPos = element.x - leftBleed;
    const elementTopPos = element.y - topBleed;
    const enableLigatures = !!element?.font?.enable_ligatures;
    const fontVariantLigatures = getFontLigatures(element, fontSize, enableLigatures);
    const adjustedElementTopPos =
      textLayerTopSpacing({ lineHeight, fontSize, body, fontFamily }) + elementTopPos;

    return {
      zIndex: element.layer,
      left: `${elementLeftPos}pt`,
      top: `${adjustedElementTopPos}pt`,
      width: `${element.width}pt`,
      height: `${element.height}pt`,
      color: `#${textColor}`,
      fontFamily: `'${fontFamily}'`,
      fontSize: `${fontSize}pt`,
      fontStyle,
      fontWeight: `${fontWeight}` as Property.FontWeight,
      fontKerning: element.font?.enable_kerning ? 'normal' : 'none',
      fontVariantLigatures,
      textAlign: horizontalAlignment,
      verticalAlign: verticalAlignment,
      letterSpacing: `${letterSpacing}em`,
      lineHeight: `${calculateLineHeight(
        isBoundingBoxAdjusted,
        element.body || '',
        fontSize,
        lineHeight
      )}pt`,
      paddingTop: 0,
      transform: `rotate(${radiansToDegrees(element.element_rotation_angle || 0)}deg)`,
      ...placeCardAlignment,
    };
  }, [body, element, bleed, foilColor, isBoundingBoxAdjusted, isPlaceCard]);

  if (!element || !element.body) {
    return null;
  }

  return (
    <div className={styles.textLayer} style={style}>
      {isCurvedTextElementView(element) ? (
        <CurvedText textElement={element} foilColor={foilColor} />
      ) : (
        body
      )}
    </div>
  );
};
